import NameLinkCell from '@/components/NameLinkCell';
import QCBadge from '@/components/UI/QCBadge';
import { sampleQueryKeys } from '@/config/queryKeys';
import { SAMPLES_ALLOWED_ROLES } from '@/config/samples';
import { defaultColDef, mainIconSize } from '@/constants';
import {
  apiFromGridApiRef,
  useCsvDownloadHandler,
  useDateFormatter,
  useOrgSamplesCsvDownload,
  useRouteProtection,
  useSamplesQcSummary,
} from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { createSetFilterParams, TEXT_FILTER_PARAMS } from '@/pages/Studies/utils/sharedColumnDefs';
import {
  QC_OUTCOME_TYPES,
  qcOutcomeLabel,
  SPECIES_VALUES,
  speciesLabel,
  type PaginatedSamplesResponse,
  type QCOutcomeType,
  type SampleWithNamesPublic,
} from '@/types';
import { applyStoredColumnVisibility } from '@/utils/applyStoredColumnVisibility';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { ColDef, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import { CloudDownload } from 'lucide-react';
import React, { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import globalStyles from '../../styles/index.module.css';

import InstrumentRunCellRenderer from './components/InstrumentRunCellRenderer';
import SamplesStatsBar from './components/SamplesStatsBar';
import StudyCellRenderer from './components/StudyCellRenderer';
import { PAGE_SIZE } from './constants/PAGE_SIZE';
import { buildDownloadOptions } from './downloadOptions';
import { useOrgId } from './helpers';
import styles from './styles.module.css';

export const SamplesPage: FC = () => {
  useRouteProtection([...SAMPLES_ALLOWED_ROLES]);
  const { t } = useTranslation('studies');
  const orgId = useOrgId();
  const gridRef = useRef<AgGridReact<SampleWithNamesPublic> | null>(null);
  const gridApiRef = useRef<GridApi<SampleWithNamesPublic> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formatDate = useDateFormatter();
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const { data: qcSummary } = useSamplesQcSummary(orgId);
  const downloadSamples = useOrgSamplesCsvDownload(orgId ?? '');

  const colDefs = useMemo<ColDef<SampleWithNamesPublic>[]>(
    () => [
      { field: 'well_position', headerName: t('columnHeaders.well'), minWidth: 80 },
      { field: 'demux_index', headerName: t('columnHeaders.demuxId'), minWidth: 90 },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) =>
          params.data ? (
            <NameLinkCell
              ariaLabel={t('goToSample')}
              className={styles.nameCell}
              fullHeight
              id={params.data.id}
              name={params.data.name}
              params={{ sampleId: params.data.id }}
              to="/samples/$sampleId"
            />
          ) : null,
        field: 'name',
        headerName: t('columnHeaders.sample'),
        minWidth: 200,
        width: 300,
      },
      { field: 'sample_group', headerName: t('columnHeaders.sampleGroup'), minWidth: 125 },
      // QC columns are real fields now, all non-nullable QCOutcomeType — no valueGetter
      // placeholders and no null branches. `sample_qc_outcome` (the collapsed view of the four
      // categories) is shipped hidden so the default grid keeps the four-category layout.
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QCBadge qc={params.value as QCOutcomeType} />
        ),
        field: 'sample_quality_qc',
        headerName: t('columnHeaders.sampleQualityQc'),
        // QC columns filter through equals/not_equals containers, so they need a SET filter —
        // inheriting defaultColDef's text filter would send 'contains' and 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('sample_quality_qc', [...QC_OUTCOME_TYPES], qcOutcomeLabel),
        minWidth: 165,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QCBadge qc={params.value as QCOutcomeType} />
        ),
        field: 'decode_qc',
        headerName: t('columnHeaders.decodeQc'),
        // QC columns filter through equals/not_equals containers, so they need a SET filter —
        // inheriting defaultColDef's text filter would send 'contains' and 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('decode_qc', [...QC_OUTCOME_TYPES], qcOutcomeLabel),
        minWidth: 100,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QCBadge qc={params.value as QCOutcomeType} />
        ),
        field: 'lane_qc',
        headerName: t('columnHeaders.laneQc'),
        // QC columns filter through equals/not_equals containers, so they need a SET filter —
        // inheriting defaultColDef's text filter would send 'contains' and 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('lane_qc', [...QC_OUTCOME_TYPES], qcOutcomeLabel),
        minWidth: 85,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QCBadge qc={params.value as QCOutcomeType} />
        ),
        field: 'controls_qc',
        headerName: t('columnHeaders.controlsQc'),
        // QC columns filter through equals/not_equals containers, so they need a SET filter —
        // inheriting defaultColDef's text filter would send 'contains' and 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('controls_qc', [...QC_OUTCOME_TYPES], qcOutcomeLabel),
        minWidth: 115,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QCBadge qc={params.value as QCOutcomeType} />
        ),
        field: 'sample_qc_outcome',
        headerName: t('columnHeaders.sampleQc'),
        // QC columns filter through equals/not_equals containers, so they need a SET filter —
        // inheriting defaultColDef's text filter would send 'contains' and 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('sample_qc_outcome', [...QC_OUTCOME_TYPES], qcOutcomeLabel),
        hide: true,
        minWidth: 120,
      },
      {
        // `warning` is a real boolean now, not derived from the removed `message` field.
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <Text as="span">{params.value === true ? t('warning_yes') : t('warning_no')}</Text>
        ),
        field: 'warning',
        headerName: t('columnHeaders.warning'),
        // Boolean container. `booleanFields` on the datasource coerces AG Grid's stringified
        // set values back to real booleans before they are sent.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('warning', ['true', 'false']),
        minWidth: 85,
      },
      {
        field: 'species',
        headerName: t('columnHeaders.species'),
        // Enum column (SpeciesFieldFilters): equals/not_equals only, so the text filter
        // defaultColDef would supply is rejected with a 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('species', [...SPECIES_VALUES], speciesLabel),
        valueFormatter: ({ value }: { value: string | null | undefined }) => (value ? speciesLabel(value) : ''),
        minWidth: 85,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <StudyCellRenderer className={styles.nameCell} data={params.data} fullHeight />
        ),
        field: 'study_name',
        headerName: t('columnHeaders.study'),
        // Explicit text filter: defaultColDef's bare `filter: true` degrades to a set filter, whose
        // value list is empty under the server-side row model — nothing to pick, so unfilterable.
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        width: 300,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <InstrumentRunCellRenderer className={styles.nameCell} data={params.data} fullHeight />
        ),
        field: 'instrument_run_name',
        headerName: t('columnHeaders.instrumentRun'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        minWidth: 160,
      },
      { field: 'assay_friendly_name', headerName: t('columnHeaders.assay') },
      {
        // TODO: switch to field: 'flow_cell' when SampleWithNamesPublic adds flow_cell
        colId: 'flow_cell',
        headerName: t('columnHeaders.flowcell'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: switch to field: 'lane' when SampleWithNamesPublic adds lane
        colId: 'lane',
        headerName: t('columnHeaders.lane'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: switch to field: 'library_group' when SampleWithNamesPublic adds library_group
        colId: 'library_group',
        headerName: t('columnHeaders.libraryGroup'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: switch to field when SampleWithNamesPublic adds instrument
        colId: 'instrument',
        headerName: t('columnHeaders.instrument'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        hide: true,
        valueFormatter: ({ value }: { value: string }) => formatDate(value),
      },
      {
        field: 'updated_at',
        headerName: t('columnHeaders.updated'),
        hide: true,
        valueFormatter: ({ value }: { value: string | null | undefined }) => (value ? formatDate(value) : ''),
      },
      {
        // TODO: expose created_by on SampleWithNamesPublic when backend supports it
        colId: 'created_by',
        headerName: t('columnHeaders.createdBy'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: expose updated_by on SampleWithNamesPublic when backend supports it
        colId: 'updated_by',
        headerName: t('columnHeaders.updatedBy'),
        hide: true,
        valueGetter: () => undefined,
      },
    ],
    [formatDate, t],
  );

  const datasource = useMemo(
    () =>
      orgId
        ? createAgGridDatasource<SampleWithNamesPublic, PaginatedSamplesResponse>(
            sampleQueryKeys.endpoints.listSearch(orgId),
            undefined,
            // `warning` is a boolean column: AG Grid serialises set-filter values as strings, so
            // without this the API would receive "true" instead of true.
            { booleanFields: ['warning'] },
          )
        : undefined,
    [orgId],
  );

  const handleGridReady = (params: { api: GridApi<SampleWithNamesPublic> }) => {
    gridApiRef.current = params.api;
    applyStoredColumnVisibility(params.api, 'samples-table');
    if (datasource) {
      params.api.setGridOption('serverSideDatasource', datasource);
    }
    sizeColumnsOnFirstVisit(params.api, 'samples-table');
  };

  // When the selected org changes, the datasource (its endpoint includes the orgId) is
  // recreated; re-apply it so the grid refetches for the new org without a remount.
  useEffect(() => {
    if (gridApiRef.current && datasource) {
      gridApiRef.current.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  const handleDownload = useCsvDownloadHandler(
    gridApiRef,
    apiFromGridApiRef,
    buildDownloadOptions,
    downloadSamples,
    t('samples_list_download_failed'),
  );

  return (
    <Stack className={styles.container} orientation="vertical">
      <Stack alignItems="center" className={styles.newTitleRow} justifyContent="space-between" orientation="horizontal">
        <Text as="h1" className={styles.title}>
          {t('title')}
        </Text>
        <Stack alignItems="center" gap={2} orientation="horizontal">
          <Button
            data-testid="samples-download-list"
            isPending={downloadSamples.isPending}
            onPress={handleDownload}
            size="md"
            visual="unstyled"
          >
            <CloudDownload size={mainIconSize} />
            {t('download_list')}
          </Button>
        </Stack>
      </Stack>

      <SamplesStatsBar passedQc={qcSummary?.num_samples_passed_qc ?? null} totalCount={totalCount} />

      <div className={styles.tableContainer}>
        <Table
          columnDefs={colDefs}
          container={{ className: globalStyles.scrollbars, ref: containerRef }}
          defaultColDef={defaultColDef}
          density="normal"
          domLayout="normal"
          enableResponsiveResize={false}
          getRowId={(params: { data: SampleWithNamesPublic }) => params.data.id}
          onGridPreDestroyed={e => flushColumnState(e.api, 'samples-table')}
          onGridReady={handleGridReady}
          onPaginationChanged={e => {
            const rowCount = e.api.paginationGetRowCount();
            if (rowCount >= 0) {
              setTotalCount(rowCount);
            }
          }}
          pagination
          paginationPageSize={PAGE_SIZE}
          ref={gridRef}
          resizeDebounceMs={150}
          rowBuffer={30}
          rowModelType="serverSide"
          tableId="samples-table"
          zebraStriping
        />
      </div>
    </Stack>
  );
};

export default SamplesPage;
